import React, {
  createContext,
  FC,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from 'react';
import i18n from 'i18next';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import api, { getCookies } from '../api/api';
import { configAccessType } from '../types/Types';
import routerLoader from '../i18n/router';
/* eslint-disable import/no-cycle */

import { SessionChecker } from '../utils/SessionChecker';
import { CookieHelper } from '../utils/CookieHelper';
import { useAuthStore } from './AuthContext';
import { LanguageHelper } from '../utils/LanguageHelper';
import { StorageHelper } from '../utils/StorageHelper';
import { HostProvider } from '../utils/HostProvider';
import { BackMappings } from '../utils/BackMappings';

export type AppContextType = {
  gtmFlag: boolean;
  lang: string;
  access: configAccessType;
  mostrarModalDatosPersonales: boolean;
  prefijosHabilitados: Array<{
    prefijo: string;
    pais: string;
  }>;
  levels: Array<string>;
  userName: string;
  changeLang: (val: string) => void;
  loadHeader: (lg: string) => void;
  getLangMenu: () => string;
  setUserAccess: (acc: configAccessType) => void;
  closeSession: (isLogoutMultidispositivo?: boolean) => void;
  setNotifications: () => void;
  changeGtmFlag: (state: boolean) => void;
  changeUserName: (name: string) => void;
  handleModalDatosPersonales: (acction: boolean) => void;
  hadlePrefijoHabilitado: (prefijosquehabilitados: Array<{}>) => void;
};

type Props = {
  children: ReactElement;
};
let defaultLang = getCookies().get('i18n') || 'es';
const dataAccess = CookieHelper.getUserData();

if (typeof dataAccess === 'object' && dataAccess !== null) {
  if (dataAccess.configAccess) defaultLang = dataAccess.configAccess.locale;
}

export const defaultContextValue = {
  gtmFlag: false,
  lang: defaultLang,
  levels: HostProvider.prepareLevels(defaultLang),
  access: (dataAccess !== null
    ? dataAccess.configAccess
    : null) as configAccessType,
  mostrarModalDatosPersonales: false,
  prefijosHabilitados: [
    {
      prefijo: '',
      pais: '',
    },
  ],
};

export const AppContext = createContext<AppContextType>(
  defaultContextValue as AppContextType,
);

export const AppProvider: FC<Props> = ({ children }) => {
  let dataUserAuth = useAuthStore();
  if (dataUserAuth === null || dataUserAuth.user.userName === '') {
    dataUserAuth =
      CookieHelper.getUserData() !== null
        ? CookieHelper.getUserData()
        : dataUserAuth;
  }
  const [lang, setLang] = useState(defaultContextValue.lang);
  const [gtmFlag, setGtmFlag] = useState(defaultContextValue.gtmFlag);
  const [levels, setLevels] = useState(defaultContextValue.levels);
  const [access, setAccess] = useState(defaultContextValue.access);
  const [userName, setUserName] = useState(dataUserAuth.user.userName);
  const [
    mostrarModalDatosPersonales,
    setMostrarModalDatosPersonales,
  ] = useState(defaultContextValue.mostrarModalDatosPersonales);
  const handleModalDatosPersonales = (acction: boolean) => {
    setMostrarModalDatosPersonales(acction);
  };
  const [prefijosHabilitados, setPrefijosHabilitados] = useState(
    defaultContextValue.prefijosHabilitados,
  );
  const hadlePrefijoHabilitado = (prefijosquehabilitados) => {
    setPrefijosHabilitados(prefijosquehabilitados);
  };
  const closeAuth = (isLogoutMultidispositivo?: boolean) => {
    SessionChecker.closeSesion(null);
    const urlLogout = `${HostProvider.getHost()}${lang}/logout?isFromFront=true${
      isLogoutMultidispositivo ? '&isLogoutImplicito=true' : ''
    }`;
    document.location.href = urlLogout;
  };
  const changeGtmFlag = (state: boolean) => {
    setGtmFlag(state);
  };
  const loadHeaderApp = (lg: string, local: boolean) => {
    const language = LanguageHelper.getCorrectedLocale(
      lg,
      undefined,
      StorageHelper.isGibraltar(),
    );
    const pageLevels = HostProvider.prepareLevels(language);
    setLang(language);
    setLevels(pageLevels);
    const token = getCookies().get('token');
    if (token) {
      const parametros = {
        view: StorageHelper.getOrigin(),
      };
      api
        .post(
          `/rest/user/header/${language}/get/header`,
          JSON.stringify(parametros),
        )
        .then((response) => {
          if (
            response.data.error &&
            response.data.errorCode === BackMappings.GO_TO_RELOGIN
          ) {
            SessionChecker.closeSessionAndGoToReLogin(null, i18n.language);
          } else {
            const accessLoad = response.data;
            // eslint-disable-next-line no-param-reassign
            dataUserAuth.configAccess = { ...accessLoad, locale: language };
            if (dataUserAuth.user && dataUserAuth.user.userName !== '') {
              dataUserAuth.user = { ...dataUserAuth.user, locale: language };
              if (accessLoad.fb && accessLoad.fb === true) {
                dataUserAuth.user.isFB = accessLoad.fb;
                CookieHelper.setFB(accessLoad.fb);
              }
            }
            setAccess({ ...accessLoad, locale: language });

            CookieHelper.setUserData(dataUserAuth);

            sessionStorage.setItem('sessionVales', '0');
            if (!local) {
              const newUrl = window.location.pathname.split('/');
              const rutas = routerLoader.LoadRouterByPath(newUrl[2], language);
              window.history.replaceState(null, '', rutas);
            }
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.log('error', error);
          // Promise.reject(error);
        });
    }
  };

  const changeUserName = (name: string) => {
    setUserName(name);
  };

  const cleanNotifications = () => {
    dataAccess.configAccess.notificacionPojo.numero = 0;
    // sessionStorage.setItem('userData', JSON.stringify(acc));
  };

  useEffect(() => {
    if (
      dataUserAuth.user.token !== '' &&
      dataUserAuth.user.token !== undefined &&
      (access === null || access === undefined)
    ) {
      const language = LanguageHelper.getCorrectedLocale(
        lang,
        undefined,
        StorageHelper.isGibraltar(),
      );
      loadHeaderApp(language, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUserAuth]);

  useEffect(() => {
    const language = LanguageHelper.getCorrectedLocale(
      lang,
      undefined,
      StorageHelper.isGibraltar(),
    );
    i18n.changeLanguage(language);
    i18n.dir(language);
    i18n.options.fallbackLng = language;
    LanguageHelper.setLanguage(language);
  }, [lang]);

  const getLangMenuApp = () => {
    return access.locale;
  };

  const value: AppContextType = {
    gtmFlag,
    handleModalDatosPersonales,
    mostrarModalDatosPersonales,
    hadlePrefijoHabilitado,
    prefijosHabilitados,
    lang,
    access,
    levels,
    userName,
    changeGtmFlag: (state: boolean) => {
      changeGtmFlag(state);
    },
    changeLang: (newLang: string) => {
      const language = LanguageHelper.getCorrectedLocale(
        newLang,
        undefined,
        StorageHelper.isGibraltar(),
      );
      setLang(language);
    },
    loadHeader: (lg: string) => {
      const language = LanguageHelper.getCorrectedLocale(
        lg,
        undefined,
        StorageHelper.isGibraltar(),
      );
      loadHeaderApp(language, false);
    },
    getLangMenu: () => {
      return getLangMenuApp();
    },
    setUserAccess: (acc: configAccessType) => {
      setAccess(acc);
    },
    closeSession: (isLogoutMultidispositivo?: boolean) => {
      closeAuth(isLogoutMultidispositivo);
    },
    setNotifications: () => {
      cleanNotifications();
    },
    changeUserName: (name) => {
      changeUserName(name);
    },
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export function useAppStore(): AppContextType {
  return useContext(AppContext);
}
/* eslint-enable  import/no-cycle */
